/**
 * Credentialed v4 API fetch core. Cookies are httpOnly + domain-split, so every
 * request sends `credentials: 'include'` and NO Authorization header. Responses
 * use the `{ success, data, error }` envelope, which this unwraps. Non-2xx (or
 * `success:false`) throws a typed ApiError subclass (see ./errors).
 *
 * 401 is handled here: one shared in-flight refresh, retry once, then hand off to
 * the app-supplied `onUnauthorized` (e.g. redirect to sign-in). The core is
 * app-agnostic — apps wire their redirect via `configureApi`.
 */
import {
  ApiError,
  UnauthorizedError,
  errorFromResponse,
} from './errors';

export interface ApiConfig {
  /** Called after a 401 that refresh could not recover (app navigates to sign-in). */
  onUnauthorized?: () => void;
  /** Path prefix for all calls. Default '' (paths already start with /api/...). */
  basePath?: string;
  /** Refresh endpoint. Default '/api/iam/auth/refresh'. */
  refreshPath?: string;
  /** Returns the entered workspace id; sent as `x-workspace-id` on each request when set. */
  getWorkspaceId?: () => string | null | undefined;
}

const config: Required<ApiConfig> = {
  onUnauthorized: () => {},
  basePath: '',
  refreshPath: '/api/iam/auth/refresh',
  getWorkspaceId: () => null,
};

export function configureApi(opts: ApiConfig): void {
  Object.assign(config, opts);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const ctype = res.headers.get('content-type') ?? '';
  if (ctype.includes('application/json')) {
    return res.json().catch(() => undefined);
  }
  await res.text().catch(() => '');
  return undefined;
}

// One in-flight refresh shared across concurrent 401s.
let pendingRefresh: Promise<boolean> | null = null;
async function attemptRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const r = await fetch(config.basePath + config.refreshPath, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      return r.ok;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        pendingRefresh = null;
      }, 0);
    }
  })();
  return pendingRefresh;
}

function isLoginCall(path: string): boolean {
  return path.includes('/api/iam/auth/login');
}

async function request<T>(path: string, init?: RequestInit, hasRetried = false): Promise<T> {
  const url = config.basePath + path;
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  // Workspace-scoped routes (out/workspace/*, kh/*) resolve the entered workspace
  // from this header. Apps supply it via configureApi({ getWorkspaceId }).
  const wsId = config.getWorkspaceId();
  if (wsId && !headers.has('x-workspace-id')) headers.set('x-workspace-id', wsId);

  const res = await fetch(url, { credentials: 'include', ...init, headers });
  const body = await parseBody(res);

  // 401 → refresh + retry once; otherwise hand off to the app.
  if (res.status === 401 && !hasRetried && !path.includes(config.refreshPath) && !isLoginCall(path)) {
    const refreshed = await attemptRefresh();
    if (refreshed) return request<T>(path, init, true);
    config.onUnauthorized();
    throw new UnauthorizedError(envelopeMessage(body) ?? 'Session expired', {
      code: envelopeCode(body),
    });
  }

  if (!res.ok) {
    throw errorFromResponse(
      res.status,
      envelopeMessage(body) ?? res.statusText ?? 'Request failed',
      envelopeCode(body),
      envelopeDetails(body),
    );
  }

  // Unwrap envelope when present.
  if (isPlainObject(body) && 'success' in body && 'data' in body) {
    const env = body as { success?: boolean; data?: T; error?: { code?: string; message?: string; details?: unknown } };
    if (env.success === false) {
      throw new ApiError(env.error?.message ?? 'Request failed', {
        status: res.status,
        code: env.error?.code,
        details: env.error?.details,
      });
    }
    return env.data as T;
  }
  return body as T;
}

function envelope(body: unknown): { code?: string; message?: string; details?: unknown } | undefined {
  if (!isPlainObject(body)) return undefined;
  if (isPlainObject(body.error)) return body.error as { code?: string; message?: string; details?: unknown };
  if (typeof body.message === 'string') return { message: body.message };
  return undefined;
}
function envelopeMessage(body: unknown): string | undefined {
  const m = envelope(body)?.message;
  return typeof m === 'string' ? m : undefined;
}
function envelopeCode(body: unknown): string | undefined {
  const c = envelope(body)?.code;
  return typeof c === 'string' ? c : undefined;
}
function envelopeDetails(body: unknown): unknown {
  return envelope(body)?.details;
}

export function withQuery(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((item) => usp.append(k, String(item)));
    else usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

/** Low-level verb client. Module namespaces (api.iam.*, …) build on this. */
export const http = {
  get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return request<T>(withQuery(path, query), { method: 'GET' });
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined });
  },
  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined });
  },
  delete<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'DELETE', body: body !== undefined ? JSON.stringify(body) : undefined });
  },
};
